import { LIMITS, MINUTES_PER_DAY, parseSettingValue, SettingKey, type Settings } from '@wiztime/core';

/** Input checks of the settings forms; the store validates again, these give early, readable errors. */

export const NameIssue = { Empty: 'empty', TooLong: 'tooLong' } as const;
export type NameIssue = (typeof NameIssue)[keyof typeof NameIssue];

export type NameCheck =
  { readonly ok: true; readonly value: string } | { readonly ok: false; readonly issue: NameIssue };

function checkName(raw: string, max: number): NameCheck {
  const value = raw.trim();
  if (value === '') return { ok: false, issue: NameIssue.Empty };
  if (value.length > max) return { ok: false, issue: NameIssue.TooLong };
  return { ok: true, value };
}

export const checkProjectName = (raw: string): NameCheck => checkName(raw, LIMITS.projectNameMax);
export const checkCheckinName = (raw: string): NameCheck => checkName(raw, LIMITS.checkinNameMax);

/** Settings edited as plain numbers in the draft rules form. */
export const DraftRuleKey = {
  RepeatDays: SettingKey.DraftRepeatDays,
  RepeatMinHits: SettingKey.DraftRepeatMinHits,
  ExpiryHours: SettingKey.DraftExpiryHours,
} as const;
export type DraftRuleKey = (typeof DraftRuleKey)[keyof typeof DraftRuleKey];

export const DraftRuleIssue = {
  /** Not a whole number. */
  NotNumber: 'notNumber',
  /** Outside the allowed range of the setting. */
  OutOfRange: 'outOfRange',
  /** The minimum number of days with the item is larger than the look-back window. */
  HitsAboveDays: 'hitsAboveDays',
} as const;
export type DraftRuleIssue = (typeof DraftRuleIssue)[keyof typeof DraftRuleIssue];

export type DraftRuleCheck =
  { readonly ok: true; readonly value: number } | { readonly ok: false; readonly issue: DraftRuleIssue };

/** Allowed ranges, shown in the error text; they mirror the schemas of `@wiztime/core`. */
export const DRAFT_RULE_RANGE: Readonly<
  Record<DraftRuleKey, { readonly min: number; readonly max: number }>
> = {
  draftRepeatDays: { min: 1, max: 14 },
  draftRepeatMinHits: { min: 1, max: 14 },
  draftExpiryHours: { min: 1, max: 720 },
};

export function checkDraftRule(key: DraftRuleKey, text: string, current: Settings): DraftRuleCheck {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return { ok: false, issue: DraftRuleIssue.NotNumber };
  const value = parseSettingValue(key, Number(trimmed));
  if (value === null) return { ok: false, issue: DraftRuleIssue.OutOfRange };
  if (key === SettingKey.DraftRepeatMinHits && value > current.draftRepeatDays) {
    return { ok: false, issue: DraftRuleIssue.HitsAboveDays };
  }
  if (key === SettingKey.DraftRepeatDays && value < current.draftRepeatMinHits) {
    return { ok: false, issue: DraftRuleIssue.HitsAboveDays };
  }
  return { ok: true, value };
}

/** Durations offered for "default block length" (minutes). */
export const DURATION_CHOICES: readonly number[] = [15, 30, 45, 60, 90, 120, 150, 180, 240, 300, 360, 480];

/** `choices` with `current` added in order when it is missing (a value set on another device or an older version). */
export function withCurrent(choices: readonly number[], current: number): number[] {
  return choices.includes(current) ? [...choices] : [...choices, current].sort((a, b) => a - b);
}

/** Clock-time choices for the sleep range: every `stepMin` minutes from 00:00 to 23:30. */
export function clockChoices(stepMin: number): number[] {
  return Array.from({ length: MINUTES_PER_DAY / stepMin }, (_, index) => index * stepMin);
}

/** Every grid point from the day start to the next day start, for template start and end times. */
function daySlots(dayStartMin: number, stepMin: number): number[] {
  const count = MINUTES_PER_DAY / stepMin;
  return Array.from({ length: count + 1 }, (_, index) => dayStartMin + index * stepMin);
}

/**
 * Start times of a draft template: on the grid of the time step, at least the minimum duration
 * before `endMin` (2026-10b §3.4 — a template is a pointer-free creation path, so the floor is
 * the minimum duration, not one step). `keep` stays even off the grid.
 */
export function templateStartChoices(
  dayStartMin: number,
  stepMin: number,
  minDurationMin: number,
  endMin: number,
  keep?: number,
): number[] {
  const slots = daySlots(dayStartMin, stepMin).filter(
    (minute) => minute <= endMin - minDurationMin && minute < dayStartMin + MINUTES_PER_DAY,
  );
  return keep === undefined ? slots : withCurrent(slots, keep);
}

/** End times of a draft template: at least the minimum duration after `startMin`, up to the next day start. */
export function templateEndChoices(
  dayStartMin: number,
  stepMin: number,
  minDurationMin: number,
  startMin: number,
  keep?: number,
): number[] {
  const slots = daySlots(dayStartMin, stepMin).filter((minute) => minute >= startMin + minDurationMin);
  return keep === undefined ? slots : withCurrent(slots, keep);
}
